import type { ComputedRef, InjectionKey, MaybeRefOrGetter } from 'vue';
import { computed, inject, provide, toValue } from 'vue';

export interface BaseFormFieldContext {
	disabled: ComputedRef<boolean | undefined>;
	disabledNotice: ComputedRef<string | undefined>;
}

export interface BaseFormControlProps {
	disabled?: boolean;
	disabledNotice?: string;
}

/**
 * @internal
 */
const baseFormFieldContextKey = Symbol('baseFormFieldContextKey') as InjectionKey<BaseFormFieldContext>;

export function provideBaseFormFieldContext(context: BaseFormFieldContext) {
	provide(baseFormFieldContextKey, context);
}

/**
 * @internal
 */
function injectBaseFormFieldContext(): BaseFormFieldContext | null {
	return inject(baseFormFieldContextKey, null);
}

/**
 * Use the form field context provided by a parent form field block.
 */
export function useBaseFormField(props?: MaybeRefOrGetter<BaseFormControlProps>) {
	const baseContext = injectBaseFormFieldContext();

	return {
		disabled: computed<boolean>(() => toValue(props)?.disabled || baseContext?.disabled.value || false), // Note: Null coalescing operator does not work here because boolean props are defaulted to false by default
		disabledNotice: computed(() => toValue(props)?.disabledNotice ?? baseContext?.disabledNotice.value),
	};
}

/**
 * Extract the block-level disabled state from input block props.
 */
export function useInputBlockDisabled(inputBlockProps: MaybeRefOrGetter<Record<string, unknown>>) {
	return computed(() => {
		const blockProps = toValue(inputBlockProps);
		return 'props' in blockProps && Boolean((blockProps.props as { disabled?: boolean } | undefined)?.disabled);
	});
}
